import { Button } from '@components/ui/button';
import { SimpleTooltip } from '@components/ui/tooltip';
import { ChevronsDownUp, Edit3, Save, X } from 'lucide-react';
import React from 'react';

export interface EditableSectionHeaderProps {
  title: string;
  canEdit: boolean;
  isEditing: boolean;
  onEditStart: () => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving?: boolean;
  showExpandButton?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  /** Optional small element rendered inline next to the title (e.g., a count pill). */
  headerBadge?: React.ReactNode;
  /** Node pinned before the title — e.g. a sidebar's collapse control, which belongs on
   *  the edge facing the content it collapses away from. */
  leadingActions?: React.ReactNode;
  /** Optional validation error shown in red next to the Save/Cancel controls. */
  errorMessage?: string;
  /**
   * Optional handler for a "View" link rendered after {@link errorMessage}.
   * Used to scroll the board to the first field that failed validation.
   */
  onErrorView?: () => void;
  /** Optional `data-testid` for the Edit button (matches `OutputBoardEditor`'s prop). */
  editTestId?: string;
  /** Optional `data-testid` for the Save button. */
  saveTestId?: string;
}

/**
 * Section header with optional Edit/Save/Cancel and Expand/Collapse controls.
 *
 * Wrapped in React.memo so it doesn't re-render when its parent re-renders for
 * unrelated reasons (e.g., audio player time updates 30+ times per second).
 */
export const EditableSectionHeader = React.memo<EditableSectionHeaderProps>(
  ({
    title,
    canEdit,
    isEditing,
    onEditStart,
    onSave,
    onCancel,
    isSaving = false,
    showExpandButton,
    isExpanded,
    onToggleExpand,
    headerBadge,
    leadingActions,
    errorMessage,
    onErrorView,
    editTestId,
    saveTestId,
  }) => (
    <div className="px-4 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 overflow-hidden">
          {leadingActions}
          {title && <h3 className="text-muted-foreground font-medium text-sm truncate">{title}</h3>}
          {headerBadge}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {canEdit && (
              <>
                {isEditing ? (
                  <>
                    <SimpleTooltip content="Save" side="bottom">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onSave}
                        disabled={isSaving}
                        className="h-5 w-5 p-0"
                        data-testid={saveTestId}
                      >
                        <Save className="w-3 h-3" />
                      </Button>
                    </SimpleTooltip>
                    <SimpleTooltip content="Discard" side="bottom">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onCancel}
                        disabled={isSaving}
                        className="h-5 w-5 p-0"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </SimpleTooltip>
                  </>
                ) : (
                  <SimpleTooltip content="Edit" side="bottom">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onEditStart}
                      className="h-5 w-5 p-0"
                      data-testid={editTestId}
                    >
                      <Edit3 className="w-3 h-3" />
                    </Button>
                  </SimpleTooltip>
                )}
              </>
            )}
            {showExpandButton && onToggleExpand && (
              <SimpleTooltip content={isExpanded ? 'Collapse All' : 'Expand All'} side="bottom">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onToggleExpand}
                  className="h-5 w-5 p-0"
                >
                  <ChevronsDownUp className="w-3 h-3" />
                </Button>
              </SimpleTooltip>
            )}
          </div>
        </div>
      </div>
      {isEditing && errorMessage && (
        <div className="mt-1 flex justify-end">
          <span className="text-xs text-destructive font-medium flex items-center gap-1">
            {errorMessage}
            {onErrorView && (
              <button
                type="button"
                onClick={onErrorView}
                className="underline underline-offset-2 hover:no-underline cursor-pointer text-destructive font-medium"
              >
                View
              </button>
            )}
          </span>
        </div>
      )}
    </div>
  ),
);
EditableSectionHeader.displayName = 'EditableSectionHeader';
